import { t as uiText } from '../i18n';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIES_NCG, SERIES_NCN } from '../features/plans/series';
import { useAuthContext } from '../context/AuthContext';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { createWorkspace } from '../features/workspace/createWorkspace';
import { updateWorkspaceIcon } from '../features/workspace/updateWorkspaceIcon';
import { validatePublishIdFormat } from '../features/workspace/updatePublishId';
import ImageCropModal from './ImageCropModal';
import './UpgradeModal.css';
import './NewWorksModal.css';

interface NewWorksModalProps {
  onClose: () => void;
}

const SERIES_OPTIONS = [
  {
    id: SERIES_NCG,
    nameKey: 'Concierge Guide',
    audienceKey: 'For site visitors',
    descriptionKey: 'A workspace where public AI answers visitors on your site.',
    suitedKey: 'When you want a question desk on your site and fewer inquiries',
    canDoKey: 'Public AI, a widget, and importing your site. Notes are where that knowledge lives',
    extraHeadingKey: 'Team features',
    extraKey: 'Notes, threads, a calendar, and AI search for your team are also available',
    extraKind: 'include',
    priceKey: 'Starts free. Paid plans from ¥14,800 /month',
    highlight: true,
  },
  {
    id: SERIES_NCN,
    nameKey: 'Collaborative Note',
    audienceKey: 'For your team',
    descriptionKey: 'A workspace for writing notes with your team and finding them inside the company.',
    suitedKey: 'When you want to keep company knowledge and look it up with your team',
    canDoKey: 'Notes, threads, a calendar, AI search for your team, and files',
    extraHeadingKey: 'Not included',
    extraKey: 'Public AI for site visitors, the widget, and importing your site',
    extraKind: 'note',
    priceKey: 'Starts free. Paid plans from ¥1,000 /month',
    highlight: false,
  },
] as const;

type SeriesChoice = (typeof SERIES_OPTIONS)[number]['id'];
type Step = 'series' | 'details';
type SlideDirection = 'forward' | 'back';

const MAX_LENGTH = 48;
const MIN_ID_LENGTH = 6;

function getDisplayLength(text: string): number {
  let length = 0;
  for (const char of text) {
    length += char.charCodeAt(0) > 127 ? 2 : 1;
  }
  return length;
}

function sanitizeId(input: string): string {
  return input.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
}

function seriesOfWorkspace(workspace: { seriesId?: string; offerId?: string } | null): SeriesChoice | null {
  if (!workspace) return null;
  const seriesId = workspace.seriesId?.trim().toUpperCase();
  if (seriesId === SERIES_NCN || seriesId === SERIES_NCG) return seriesId;
  const offerId = workspace.offerId?.trim().toLowerCase() ?? '';
  if (offerId.startsWith('ncg:')) return SERIES_NCG;
  if (offerId.startsWith('ncn:')) return SERIES_NCN;
  return null;
}

const NewWorksModal: React.FC<NewWorksModalProps> = ({ onClose }) => {
  const { user } = useAuthContext();
  const { selectedWorkspace, refresh } = useWorkspaceContext();
  const navigate = useNavigate();
  const titleId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const userTypedWorkspaceId = useRef(false);
  const [step, setStep] = useState<Step>('series');
  const [direction, setDirection] = useState<SlideDirection>('forward');
  const [animate, setAnimate] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<SeriesChoice | null>(null);
  const [name, setName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [publishId, setPublishId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);

  const isNcg = selectedSeries === SERIES_NCG;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (showCropModal) {
        setShowCropModal(false);
        setSelectedImageFile(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, showCropModal]);

  useEffect(() => {
    if (!animate) return;
    headingRef.current?.focus();
  }, [animate, step, selectedSeries]);

  const resetDetails = () => {
    setName('');
    setWorkspaceId('');
    setPublishId('');
    setErrorMsg('');
    setIconUrl(null);
    setIconFile(null);
    setSelectedImageFile(null);
    setShowCropModal(false);
    userTypedWorkspaceId.current = false;
  };

  const openDetails = (series: SeriesChoice) => {
    setSelectedSeries(series);
    resetDetails();
    setDirection('forward');
    setAnimate(true);
    setStep('details');
  };

  const backToSeries = () => {
    setDirection('back');
    setAnimate(true);
    setStep('series');
    resetDetails();
  };

  const handleNameChange = (value: string) => {
    if (getDisplayLength(value) > MAX_LENGTH) return;
    setName(value);
    setErrorMsg('');
    if (!userTypedWorkspaceId.current) {
      setWorkspaceId(sanitizeId(value).slice(0, MAX_LENGTH));
    }
  };

  const handleIdChange = (value: string) => {
    userTypedWorkspaceId.current = true;
    setWorkspaceId(sanitizeId(value).slice(0, MAX_LENGTH));
    setErrorMsg('');
  };

  const handlePublishIdChange = (value: string) => {
    setPublishId(value);
    setErrorMsg('');
  };

  const handleCreate = async () => {
    if (!user || !selectedSeries || !name.trim() || !workspaceId) return;
    if (workspaceId.length < MIN_ID_LENGTH) {
      setErrorMsg(uiText("Enter a workspace ID of at least {{MIN_ID_LENGTH}} characters", { MIN_ID_LENGTH }));
      return;
    }
    const trimmedPublishId = publishId.trim();
    if (isNcg) {
      const publishCheck = validatePublishIdFormat(trimmedPublishId);
      if (!publishCheck.valid) {
        setErrorMsg(publishCheck.error || uiText("publishID may contain only letters, numbers, and hyphens (-), and must be 3-50 characters"));
        return;
      }
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      const result = await createWorkspace({
        id: workspaceId,
        name: name.trim(),
        createdBy: user.uid,
        visibility: 0,
        planId: 'free',
        seriesId: selectedSeries,
        publishId: isNcg ? trimmedPublishId : undefined,
      });
      if (!result.success) {
        setErrorMsg(result.error || uiText("Failed to create the workspace"));
        return;
      }
      if (iconFile) {
        try {
          await updateWorkspaceIcon({
            workspaceId,
            imageFile: iconFile,
            currentIconPath: null,
          });
        } catch (error) {
          console.error('ワークスペースアイコンの保存に失敗:', error);
        }
      }
      await refresh();
      alert(result.warning || uiText("Workspace created"));
      navigate(`/${workspaceId}`);
      onClose();
    } catch (error) {
      console.error('ワークスペース作成エラー:', error);
      setErrorMsg(uiText("Failed to create the workspace"));
    } finally {
      setSubmitting(false);
    }
  };

  const openSeries = seriesOfWorkspace(selectedWorkspace);
  const seriesNameKey = selectedSeries === SERIES_NCG ? 'Concierge Guide' : 'Collaborative Note';
  const publishIdCheck = publishId.trim() ? validatePublishIdFormat(publishId.trim()) : null;
  const idError = workspaceId && workspaceId.length < MIN_ID_LENGTH
    ? uiText("Enter a workspace ID of at least {{MIN_ID_LENGTH}} characters", { MIN_ID_LENGTH })
    : '';
  const publishError = isNcg && publishIdCheck && !publishIdCheck.valid
    ? (publishIdCheck.error || '')
    : '';
  const canCreate = Boolean(
    name.trim() &&
    workspaceId.length >= MIN_ID_LENGTH &&
    selectedSeries &&
    (!isNcg || publishIdCheck?.valid) &&
    !submitting
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={[
          'nwm-dialog',
          step === 'details' ? 'is-details' : '',
          selectedSeries === SERIES_NCG ? 'is-ncg' : '',
          selectedSeries === SERIES_NCN ? 'is-ncn' : '',
        ].filter(Boolean).join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="nwm-header">
          <h2
            id={titleId}
            ref={headingRef}
            tabIndex={-1}
            className="nwm-title"
          >
            {step === 'details'
              ? uiText("Create workspace")
              : uiText("Add a workspace")}
          </h2>
          <div className="nwm-header-actions">
            {step === 'details' && (
              <button
                type="button"
                className="nwm-back"
                onClick={backToSeries}
                disabled={submitting}
              >
                {uiText("← Back")}
              </button>
            )}
            <button type="button" className="nwm-close" onClick={onClose}>
              {uiText("Close")}
            </button>
          </div>
        </div>

        <div className="nwm-stage">
          <div className={animate ? `nwm-panel ${direction}` : 'nwm-panel'} key={`${step}-${selectedSeries ?? 'none'}`}>
            {step === 'details' && selectedSeries ? (
              <form
                className="nwm-details-step"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleCreate();
                }}
              >
                <p className="nwm-plan-intro">{uiText("Enter the details for this workspace.")}</p>
                <div className="nwm-summary">
                  <div>
                    <span>{uiText("Series:")}</span>
                    <strong>{uiText(seriesNameKey)}</strong>
                  </div>
                  <div>
                    <span>{uiText("Plan:")}</span>
                    <strong>free</strong>
                    <span className="nwm-summary-price">{uiText("Free")}</span>
                  </div>
                </div>
                <p className="nwm-hint">{uiText("You can change the plan after creation.")}</p>

                <label className="nwm-field" htmlFor="nwm-name">
                  <span>{uiText("Workspace name")}<span className="nwm-required">*</span></span>
                  <input
                    id="nwm-name"
                    type="text"
                    value={name}
                    onChange={(event) => handleNameChange(event.target.value)}
                    placeholder={uiText("Example: a sales team")}
                    disabled={submitting}
                  />
                  <span className="nwm-count">{getDisplayLength(name)}/{MAX_LENGTH}</span>
                </label>

                <label className="nwm-field" htmlFor="nwm-id">
                  <span>{uiText("Workspace ID")}<span className="nwm-required">*</span></span>
                  <input
                    id="nwm-id"
                    type="text"
                    value={workspaceId}
                    onChange={(event) => handleIdChange(event.target.value)}
                    placeholder={uiText("Letters and numbers only, at least {{MIN_ID_LENGTH}} characters (auto-generated, editable)", { MIN_ID_LENGTH })}
                    disabled={submitting}
                  />
                  <span className="nwm-hint">{uiText("This cannot be changed after creation.")}</span>
                  <span className="nwm-count">{workspaceId.length}/{MAX_LENGTH}</span>
                </label>

                {isNcg && (
                  <label className="nwm-field" htmlFor="nwm-publish-id">
                    <span>{uiText("Public ID")}<span className="nwm-required">*</span></span>
                    <input
                      id="nwm-publish-id"
                      type="text"
                      value={publishId}
                      onChange={(event) => handlePublishIdChange(event.target.value)}
                      placeholder={uiText("Letters, numbers, and hyphens, 3–50 characters")}
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      className="nwm-text-button"
                      onClick={() => handlePublishIdChange(workspaceId)}
                      disabled={submitting || !workspaceId}
                    >
                      {uiText("Use the same value as the workspace ID")}
                    </button>
                  </label>
                )}

                <div className="nwm-field">
                  <span>{uiText("Workspace icon")}</span>
                  <div className="nwm-icon-row">
                    <div className="nwm-icon-preview" aria-hidden="true">
                      {iconUrl ? (
                        <img src={iconUrl} alt="" />
                      ) : (
                        <span>{name.trim().charAt(0) || 'A'}</span>
                      )}
                    </div>
                    <div>
                      <div className="nwm-icon-label">{uiText("Preview")}</div>
                      <div className="nwm-hint">{iconUrl ? uiText("Image icon") : uiText("Default icon")}</div>
                    </div>
                  </div>
                  <div className="nwm-icon-actions">
                    <button
                      type="button"
                      className="nwm-secondary"
                      disabled={submitting}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = () => {
                          const file = input.files?.[0];
                          if (!file) return;
                          setSelectedImageFile(file);
                          setShowCropModal(true);
                        };
                        input.click();
                      }}
                    >
                      {uiText("Select file")}
                    </button>
                    <span className="nwm-file-name">{iconFile ? iconFile.name : uiText("None selected")}</span>
                  </div>
                  {iconUrl && (
                    <button
                      type="button"
                      className="nwm-text-button is-danger"
                      disabled={submitting}
                      onClick={() => {
                        setIconUrl(null);
                        setIconFile(null);
                        setSelectedImageFile(null);
                      }}
                    >
                      {uiText("Delete image")}
                    </button>
                  )}
                  <span className="nwm-hint">{uiText("Select an image to display as the workspace icon")}</span>
                </div>

                {(idError || publishError || errorMsg) && (
                  <div className="nwm-error">{idError || publishError || errorMsg}</div>
                )}

                <button type="submit" className="nwm-submit" disabled={!canCreate}>
                  {submitting ? uiText("Creating...") : uiText("Create")}
                </button>
              </form>
            ) : (
              <div className="nwm-series-step">
                <p className="nwm-series-lead-in">
                  {uiText("Which workspace do you want to create?")}
                </p>
                <p className="nwm-series-hint">
                  {uiText("Workspaces start on the free plan. To use a paid plan, create the workspace first, then change the plan in workspace settings.")}
                </p>
                <p className="nwm-series-hint">
                  {uiText("You can change the series later in some cases. Start with the use you need.")}
                </p>
                <div className="nwm-series-options">
                  {SERIES_OPTIONS.map((option) => {
                    const isCurrent = openSeries === option.id && !!selectedWorkspace;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => openDetails(option.id)}
                        className={[
                          'nwm-series-card',
                          option.highlight ? 'is-highlight' : '',
                          isCurrent ? 'is-current' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        <span className="nwm-series-audience">{uiText(option.audienceKey)}</span>
                        <div className="nwm-series-name">{uiText(option.nameKey)}</div>
                        <p className="nwm-series-description">{uiText(option.descriptionKey)}</p>
                        <div className="nwm-series-block">
                          <div className="nwm-series-heading">{uiText("Good for")}</div>
                          <p className="nwm-series-text">{uiText(option.suitedKey)}</p>
                        </div>
                        <div className="nwm-series-block">
                          <div className="nwm-series-heading">{uiText("What you can do")}</div>
                          <p className="nwm-series-text">{uiText(option.canDoKey)}</p>
                        </div>
                        <div className={`nwm-series-block is-${option.extraKind}`}>
                          <div className="nwm-series-heading">{uiText(option.extraHeadingKey)}</div>
                          <p className="nwm-series-text">{uiText(option.extraKey)}</p>
                        </div>
                        <div className="nwm-series-price">
                          <div className="nwm-series-heading">{uiText("Price guide")}</div>
                          <p className="nwm-series-text">{uiText(option.priceKey)}</p>
                        </div>
                        <div className="nwm-series-footer">
                          {isCurrent && (
                            <div className="nwm-current">
                              <div className="nwm-current-label">{uiText("Your current workspace is this one")}</div>
                              <div className="nwm-current-name">{selectedWorkspace.name}</div>
                            </div>
                          )}
                          <span className="nwm-series-continue">{uiText("Continue with this series")}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          cropType="square"
          overlayZIndex={100000}
          onCropComplete={(croppedFile, dataUrl) => {
            setIconFile(croppedFile);
            setIconUrl(dataUrl);
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
        />
      )}
    </div>
  );
};

export default NewWorksModal;
